import { CalendarDays, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { IntegrationJob, IntegrationRun, IntegrationStatus, Severity, SourceType } from "../../shared/types";
import { SeverityBadge } from "../components/common/SeverityBadge";
import { StateBlock } from "../components/common/StateBlock";
import { StatusChip } from "../components/common/StatusChip";
import { RunsGanttChart, type GanttGroupBy } from "../components/runs/RunsGanttChart";
import { getJobs } from "../services/jobService";
import { getRuns } from "../services/runService";
import { buildProjectedRuns } from "../utils/runProjection";
import { formatDateTime, formatDuration } from "../utils/status";

function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoString(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function isTriggerMarkerRun(run: IntegrationRun): boolean {
  return Boolean(
    run.externalRunId &&
    run.id.includes("-trigger-") &&
    run.actualStartTime &&
    run.endTime &&
    run.actualStartTime === run.endTime
  );
}

interface RunsMonitoringPageProps {
  refreshKey: number;
  onOpenJob: (jobId: string) => void;
}

const statuses: Array<IntegrationStatus | "ALL"> = [
  "ALL",
  "SCHEDULED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
  "MISSING",
  "LATE",
  "LONG_RUNNING",
  "UNKNOWN"
];
const sourceTypes: Array<SourceType | "ALL"> = ["ALL", "SYNAPSE", "ACR", "SQL_ETL", "POWER_BI", "MARKETING", "CUSTOM", "AZURE_MONITOR"];
const severities: Array<Severity | "ALL"> = ["ALL", "LOW", "MEDIUM", "HIGH", "CRITICAL"];
const ganttGroupOptions: Array<{ value: GanttGroupBy; label: string }> = [
  { value: "SOURCE_TYPE", label: "Source Type" },
  { value: "JOB", label: "Job" }
];

export function RunsMonitoringPage({ refreshKey, onOpenJob }: RunsMonitoringPageProps) {
  const [runs, setRuns] = useState<IntegrationRun[]>([]);
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [statusFilter, setStatusFilter] = useState<IntegrationStatus | "ALL">("ALL");
  const [sourceFilter, setSourceFilter] = useState<SourceType | "ALL">("ALL");
  const [severityFilter, setSeverityFilter] = useState<Severity | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [dateStart, setDateStart] = useState(() => daysAgoString(1));
  const [dateEnd, setDateEnd] = useState(todayString);
  const [ganttGroupBy, setGanttGroupBy] = useState<GanttGroupBy>("SOURCE_TYPE");
  const [snapshotAt, setSnapshotAt] = useState(() => new Date().toISOString());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(undefined);

    Promise.all([getRuns({ dateStart, dateEnd }), getJobs()])
      .then(([nextRuns, nextJobs]) => {
        if (isMounted) {
          setRuns(nextRuns);
          setJobs(nextJobs);
          setSnapshotAt(new Date().toISOString());
        }
      })
      .catch((err: Error) => {
        if (isMounted) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dateEnd, dateStart, refreshKey]);

  const projectedRuns = useMemo(() => buildProjectedRuns({
    jobs,
    runs,
    dateStart,
    dateEnd,
    now: new Date(snapshotAt)
  }), [dateEnd, dateStart, jobs, runs, snapshotAt]);

  const filteredRuns = useMemo(() => {
    return projectedRuns.filter((run) => {
      const matchesStatus = statusFilter === "ALL" || run.status === statusFilter;
      const matchesSource = sourceFilter === "ALL" || run.sourceType === sourceFilter;
      const matchesSeverity = severityFilter === "ALL" || run.severity === severityFilter;
      const matchesSearch = run.jobName.toLowerCase().includes(search.toLowerCase());
      const matchesDateStart = !dateStart || run.expectedStartTime >= `${dateStart}T00:00:00Z`;
      const matchesDateEnd = !dateEnd || run.expectedStartTime <= `${dateEnd}T23:59:59Z`;
      return matchesStatus && matchesSource && matchesSeverity && matchesSearch && matchesDateStart && matchesDateEnd;
    });
  }, [dateEnd, dateStart, projectedRuns, search, severityFilter, sourceFilter, statusFilter]);

  const ganttRuns = useMemo(() => {
    return filteredRuns.filter((run) => !isTriggerMarkerRun(run));
  }, [filteredRuns]);

  if (isLoading) {
    return <StateBlock type="loading" title="Loading runs" message="Building run history and expected schedule slots." />;
  }

  if (error) {
    return <StateBlock type="error" title="Runs unavailable" message={error} />;
  }

  return (
    <section className="page-stack">
      <div className="page-heading">
        <div>
          <p>Run operations</p>
          <h2>Runs Monitoring</h2>
        </div>
        <span className="count-pill">{filteredRuns.length} runs</span>
      </div>

      <section className="gantt-toolbar" aria-label="Gantt grouping">
        <span>Group Gantt by</span>
        <div className="gantt-group-control" role="group" aria-label="Gantt group mode">
          {ganttGroupOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={ganttGroupBy === option.value ? "active" : undefined}
              onClick={() => setGanttGroupBy(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      <RunsGanttChart runs={ganttRuns} groupBy={ganttGroupBy} onOpenJob={onOpenJob} />

      <section className="filter-bar" aria-label="Run filters">
        <label className="search-field">
          <Search size={16} aria-hidden="true" />
          <input
            aria-label="Search by job name"
            placeholder="Search job name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <select aria-label="Status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as IntegrationStatus | "ALL")}>
          {statuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <select aria-label="Source type filter" value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value as SourceType | "ALL")}>
          {sourceTypes.map((sourceType) => (
            <option key={sourceType} value={sourceType}>{sourceType}</option>
          ))}
        </select>
        <select aria-label="Severity filter" value={severityFilter} onChange={(event) => setSeverityFilter(event.target.value as Severity | "ALL")}>
          {severities.map((severity) => (
            <option key={severity} value={severity}>{severity}</option>
          ))}
        </select>
        <label className="date-field">
          <CalendarDays size={16} aria-hidden="true" />
          <input aria-label="Date range start" type="date" value={dateStart} onChange={(event) => setDateStart(event.target.value)} />
        </label>
        <label className="date-field">
          <CalendarDays size={16} aria-hidden="true" />
          <input aria-label="Date range end" type="date" value={dateEnd} onChange={(event) => setDateEnd(event.target.value)} />
        </label>
      </section>

      <section className="panel table-panel">
        {filteredRuns.length === 0 ? (
          <StateBlock type="empty" title="No matching runs" message="Adjust filters to broaden the monitoring window." />
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Job Name</th>
                  <th>Source Type</th>
                  <th>Source Name</th>
                  <th>Expected Start Time</th>
                  <th>Actual Start Time</th>
                  <th>End Time</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Severity</th>
                  <th>Last Error</th>
                  <th>Owner</th>
                  <th>Last Checked At</th>
                </tr>
              </thead>
              <tbody>
                {filteredRuns.map((run) => (
                  <tr key={run.id} onClick={() => onOpenJob(run.jobId)} tabIndex={0}>
                    <td><strong>{run.jobName}</strong></td>
                    <td>{run.sourceType}</td>
                    <td>{run.sourceName}</td>
                    <td className="date-cell">{formatDateTime(run.expectedStartTime)}</td>
                    <td className="date-cell">{formatDateTime(run.actualStartTime)}</td>
                    <td className="date-cell">{formatDateTime(run.endTime)}</td>
                    <td>{formatDuration(run.durationMinutes)}</td>
                    <td><StatusChip status={run.status} /></td>
                    <td><SeverityBadge severity={run.severity} /></td>
                    <td className="error-cell">{run.lastError || "-"}</td>
                    <td>{run.owner}</td>
                    <td className="date-cell">{formatDateTime(run.lastCheckedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
